import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Filter } from "lucide-react";
import { useMemo, useState } from "react";
import PlatformBadge from "../../../components/ui/common/platform-badge";
import ServiceTypePill from "../../../components/ui/common/service-type-pill";
import XBadge from "../../../components/XBadge";
import useOverviewStore from "../store/overview";
const FilterDropdown = () => {
  const [open, setOpen] = useState(false);
  const { filters, setFilters, resetFilters, services } = useOverviewStore();
  const [selectedPlatforms, setSelectedPlatforms] = useState(filters.platforms);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState(filters.serviceTypes);
  const [selectedTags, setSelectedTags] = useState(filters.tags);
  const [currentTag, setCurrentTag] = useState("");

  const platforms = useMemo(
    () => [...new Set(services.map((s) => s.platform).filter(Boolean))],
    [services]
  );
  const serviceTypes = ["PRODUCER", "CONSUMER", "CONSUMER_PRODUCER"];

  const handlePlatformChange = (platform) => {
    setSelectedPlatforms((prevPlatforms) =>
      prevPlatforms.includes(platform)
        ? prevPlatforms.filter((p) => p !== platform)
        : [...prevPlatforms, platform]
    );
  };

  const handleServiceTypeChange = (serviceType) => {
    setSelectedServiceTypes((prevTypes) =>
      prevTypes.includes(serviceType)
        ? prevTypes.filter((t) => t !== serviceType)
        : [...prevTypes, serviceType]
    );
  };

  const handleTagChange = (tag) => {
    setSelectedTags((prevTags) =>
      prevTags.includes(tag)
        ? prevTags.filter((t) => t !== tag)
        : [...prevTags, tag]
    );
  };

  const handleAddTag = () => {
    if (currentTag.trim() !== "" && !selectedTags.includes(currentTag)) {
      setSelectedTags([...selectedTags, currentTag]);
      setCurrentTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setSelectedTags(selectedTags.filter((tag) => tag !== tagToRemove));
  };

  const handleInputChange = (event) => {
    setCurrentTag(event.target.value);
  };

  const handleReset = () => {
    setSelectedPlatforms([]);
    setSelectedServiceTypes([]);
    setSelectedTags([]);
    resetFilters();
  };

  const handleApply = () => {
    setFilters({
      platforms: selectedPlatforms,
      serviceTypes: selectedServiceTypes,
      tags: selectedTags,
    });
    setOpen(false);
  };

  const activeFilterCount =
    filters.platforms.length + filters.serviceTypes.length + filters.tags.length;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`rounded h-auto gap-1.5 bg-muted ${activeFilterCount > 0 ? "border border-primary text-white px-2 w-auto" : ""}`}
        >
          <Filter className={activeFilterCount > 0 ? "text-white" : "text-gray-400"} />
          {activeFilterCount > 0 && (
            <span>{activeFilterCount} applied</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[35rem] bg-navbarBackground"
        align="end"
      >
        <div className="p-2 flex items-baseline gap-4 justify-start">
          <div className="flex justify-start items-baseline gap-4">
            <div className="space-y-2">
              <span className="text-xs font-semibold">Platform</span>
              <div className="grid grid-cols-1 gap-2">
                {platforms.map((platform) => (
                  <div className="flex items-center space-x-2" key={platform}>
                    <Checkbox
                      id={`platform-filter-${platform}`}
                      checked={selectedPlatforms.includes(platform)}
                      onCheckedChange={() => handlePlatformChange(platform)}
                    />
                    <label
                      htmlFor={`platform-filter-${platform}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      <PlatformBadge platform={platform} />
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <span className="text-xs font-semibold">Service Type</span>
              <div className="grid grid-cols-1 gap-2">
                {serviceTypes.map((type) => (
                  <div className="flex items-center space-x-2" key={type}>
                    <Checkbox
                      key={type}
                      checked={selectedServiceTypes.includes(type)}
                      onCheckedChange={() => handleServiceTypeChange(type)}
                      className="uppercase"
                    />
                    <ServiceTypePill type={type} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2 w-[60%]">
            <span className="text-xs font-semibold">Tags</span>
            <div className="">
              <Input
                value={currentTag}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddTag();
                  }
                }}
                placeholder="Search..."
                className="h-8 w-full"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedTags.map((tag) => (
                <XBadge
                  key={tag}
                  label={`#${tag}`}
                  handleRemove={handleRemoveTag}
                />
              ))}
            </div>
          </div>
        </div>

        <DropdownMenuSeparator />
        <div className="p-2 flex justify-between items-center">
          <Button
            variant="borderless"
            className="text-white"
            size="compact"
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button variant="borderless" size="compact" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FilterDropdown;
